import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Appointment, ProgressStep } from "../../../types";
import { appointmentsApi, authService, companyApi, servicesApi } from "../../../services";
import { useCompany } from "../../../contexts/CompanyContext";
import { AdminPageHeaderWithBack } from "../shared/AdminPageHeaderWithBack";
import { Card } from "../../shared/Card";
import { ActionButton, Avatar } from "../../shared";
import { Icons } from "../../shared/Icons";
import { CarBrandLogo } from "../../shared/CarBrandLogo";
import { Modal } from "../../shared/Modal";
import { StepProfessionalsModal } from "./StepProfessionalsModal";
import { UpdatePaymentModal } from "./UpdatePaymentModal";
import { AppointmentNotesModal } from "./AppointmentNotesModal";
import { AppointmentEndModal } from "./AppointmentEndModal";
import { CreateAppointmentModal } from "./CreateAppointmentModal";
import { AppointmentFollowUpPanel } from "./AppointmentFollowUpPanel";
import { DownloadBudgetPdfModal } from "../AdminBudgets/DownloadBudgetPdfModal";
import type { BudgetPdfPriceOptions } from "../AdminBudgets/DownloadBudgetPdfModal";

import {
  getStatusBadgeColor,
  translateStatus,
  getPaymentMethodColor,
  getCarEffortColor,
  appointmentSpansMultipleDays,
  toAppointmentDateOnly,
} from "../../../utils/appointmentUtils";
import { downloadAppointmentPdf } from "../../../utils/appointmentPdf";
import { formatDateTime, formatTime, isBirthdayToday, createDateInBrazil, TIMEZONE, getTodayInBrazil, formatDateShort } from "../../../utils/dateUtils";
import {
  sendWhatsAppMessage,
  getCompletionMessage,
  getWhatsAppButtonStyles,
  getWhatsAppIconStyles,
  WHATSAPP_ICON_PATH,
} from "../../../utils/whatsappUtils";

export const AppointmentDetails: React.FC = () => {
  const { appointmentId } = useParams<{
    appointmentId: string;
  }>();
  const { company } = useCompany();
  const navigate = useNavigate();
  const userData = authService.getUserData();
  const isCompanyAdmin = userData?.userType === "company";
  const isProfessionalCarView =
    userData?.userType === "professional" && company?.category === "car";
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stepLoading, setStepLoading] = useState<Record<number, boolean>>({});
  const [finishLoading, setFinishLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [professionalModalOpen, setProfessionalModalOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [endDateModalOpen, setEndDateModalOpen] = useState(false);
  const [addStepModalOpen, setAddStepModalOpen] = useState(false);
  const [addStepLinks, setAddStepLinks] = useState<{ id: number; subServiceName: string; price: number }[]>([]);
  const [addStepLoading, setAddStepLoading] = useState(false);
  const [addStepSaving, setAddStepSaving] = useState<number | null>(null);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);
  const [pauseModalStep, setPauseModalStep] = useState<ProgressStep | null>(null);
  const [selectedPauseReason, setSelectedPauseReason] = useState<string | null>(null);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [detailsTab, setDetailsTab] = useState<"progress" | "followUp">(
    "progress"
  );
  const [expiredFollowUpCount, setExpiredFollowUpCount] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [preFilledAppointmentData, setPreFilledAppointmentData] = useState<{
    clientId?: number;
    serviceId?: number;
    clientCarId?: number;
    subServiceIds?: number[];
  }>();
  const [followUpRefreshKey, setFollowUpRefreshKey] = useState(0);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfDownloadLoading, setPdfDownloadLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const scrollRestoreRef = useRef<number | null>(null);

  const PAUSE_REASONS = [
    "Almoço",
    "Atendimento ao cliente",
    "Café",
    "Entrega de carro",
    "Quebra de equipamento",
    "Troca de Prioridade de Veículo"
  ] as const;

  const fetchAppointmentDetails = useCallback(async (options?: { preserveScroll?: boolean }) => {
    if (!appointmentId) return;

    if (options?.preserveScroll) {
      scrollRestoreRef.current = window.scrollY;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await appointmentsApi.getAppointmentById(
        parseInt(appointmentId)
      );

      if (response.success && response.data) {
        setAppointment(response.data);
      } else {
        setError(response.error || "Erro ao carregar detalhes do agendamento");
      }
    } catch (err) {
      console.error("Error fetching appointment details:", err);
      setError("Erro ao carregar detalhes do agendamento");
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    fetchAppointmentDetails();
  }, [fetchAppointmentDetails]);

  // Restore scroll after a refetch that requested preservation (e.g. after closing modal or ending step)
  useEffect(() => {
    if (scrollRestoreRef.current !== null && appointment) {
      const y = scrollRestoreRef.current;
      scrollRestoreRef.current = null;
      requestAnimationFrame(() => {
        window.scrollTo(0, y);
      });
    }
  }, [appointment]);

  // Update current time every second for live timers
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const getStepStatus = (step: ProgressStep) => {
    if (step.endTime) return "completed";
    if (step.startTime) return "in_progress";
    return "pending";
  };

  const getStepStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 border-green-200 dark:bg-green-950 dark:text-green-200 dark:border-green-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-800";
      case "pending":
        return "bg-gray-100 text-gray-600 border-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600";
    }
  };

  const getStepStatusIcon = (status: string): React.ReactNode => {
    const sx = { fontSize: 20 };
    switch (status) {
      case "completed":
        return <Icons.Check sx={sx} />;
      case "in_progress":
        return <Icons.Refresh sx={sx} />;
      case "pending":
        return <Icons.Schedule sx={sx} />;
      default:
        return <Icons.Schedule sx={sx} />;
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return phone;

    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, "");

    // Apply Brazilian phone number formatting
    if (cleaned.length === 11) {
      // Mobile: (11) 99999-9999
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(
        7
      )}`;
    } else if (cleaned.length === 10) {
      // Landline: (11) 9999-9999
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(
        6
      )}`;
    } else if (cleaned.length === 8) {
      // Local: 9999-9999
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
    }

    // Return original if doesn't match expected formats
    return phone;
  };

  const formatDuration = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getStepDuration = (step: ProgressStep) => {
    if (step.endTime && step.startTime) {
      // Completed step
      return (
        new Date(step.endTime).getTime() - new Date(step.startTime).getTime()
      );
    } else if (step.startTime && !step.endTime) {
      // In progress step
      return currentTime.getTime() - new Date(step.startTime).getTime();
    }
    return 0;
  };

  const renderStepTimer = (step: ProgressStep, status: string) => {
    const duration = getStepDuration(step);

    if (status === "pending") {
      return null;
    }

    const isInProgress = status === "in_progress";
    const bgColor = status === "completed" ? "bg-green-100" : "bg-blue-100";
    const textColor =
      status === "completed" ? "text-green-800" : "text-blue-800";
    const borderColor =
      status === "completed" ? "border-green-300" : "border-blue-300";
    const icon = status === "completed" ? "⏱️" : "⏰";

    return (
      <div
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${bgColor} ${textColor} ${borderColor} mb-2`}
      >
        <span>{icon}</span>
        <span className={isInProgress ? "animate-pulse" : ""}>
          {formatDuration(duration)}
        </span>
        {isInProgress && <span className="ml-1 text-[10px]">em andamento</span>}
      </div>
    );
  };

  const getSumOfStepDurations = () => {
    if (!appointment?.progressSteps?.length) return 0;
    return appointment.progressSteps.reduce(
      (acc, step) => acc + getStepDuration(step),
      0
    );
  };

  const getAppointmentTotalDuration = () => {
    if (!appointment?.progressSteps?.length) return 0;

    // Find the earliest start time and latest end time
    const startTimes = appointment.progressSteps
      .filter((step) => step.startTime)
      .map((step) => new Date(step.startTime!).getTime());

    if (startTimes.length === 0) return 0;

    const minStartTime = Math.min(...startTimes);

    // Check if any step is still in progress
    const inProgressSteps = appointment.progressSteps.filter(
      (step) => step.startTime && !step.endTime
    );
    const completedSteps = appointment.progressSteps.filter(
      (step) => step.endTime
    );

    if (inProgressSteps.length > 0) {
      // If there are in-progress steps, use current time as end
      return currentTime.getTime() - minStartTime;
    } else if (completedSteps.length > 0) {
      // If all started steps are completed, use the latest end time
      const endTimes = completedSteps.map((step) =>
        new Date(step.endTime!).getTime()
      );
      const maxEndTime = Math.max(...endTimes);
      return maxEndTime - minStartTime;
    }

    return 0;
  };

  const renderAppointmentTimer = () => {
    const duration = getAppointmentTotalDuration();

    if (duration === 0) {
      return (
        <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border bg-gray-100 text-gray-600 border-gray-300">
          <span>⏱️</span>
          <span>Não iniciado</span>
        </div>
      );
    }

    const hasInProgress = appointment?.progressSteps?.some(
      (step) => step.startTime && !step.endTime
    );
    const bgColor = hasInProgress ? "bg-blue-100" : "bg-green-100";
    const textColor = hasInProgress ? "text-blue-800" : "text-green-800";
    const borderColor = hasInProgress ? "border-blue-300" : "border-green-300";
    const icon = hasInProgress ? "⏰" : "⏱️";

    return (
      <div
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${bgColor} ${textColor} ${borderColor}`}
      >
        <span>{icon}</span>
        <span>
          {formatDuration(duration)}
        </span>
        {hasInProgress && <span className="ml-1 text-xs">em andamento</span>}
      </div>
    );
  };

  const handleEndStep = async (stepId: number) => {
    setStepLoading((prev) => ({ ...prev, [stepId]: true }));
    try {
      const response = await appointmentsApi.endProgressStep(stepId);
      if (response.success) {
        await fetchAppointmentDetails({ preserveScroll: true });
      } else {
        setError(response.error || "Erro ao finalizar etapa");
      }
    } catch (err) {
      setError("Erro ao finalizar etapa");
    } finally {
      setStepLoading((prev) => ({ ...prev, [stepId]: false }));
    }
  };

  const handlePauseStep = async (stepId: number, note: string) => {
    setPauseLoading(true);
    setError(null);
    try {
      const response = await appointmentsApi.endProgressStep(stepId, note);
      if (response.success) {
        setPauseModalStep(null);
        await fetchAppointmentDetails({ preserveScroll: true });
      } else {
        setError(response.error || "Erro ao pausar etapa");
      }
    } catch (err) {
      setError("Erro ao pausar etapa");
    } finally {
      setPauseLoading(false);
    }
  };

  const handleManageProfessionals = (stepId: string, stepName: string) => {
    setSelectedStep({ id: stepId, name: stepName });
    setProfessionalModalOpen(true);
  };

  const handleCloseProfessionalModal = () => {
    setProfessionalModalOpen(false);
    setSelectedStep(null);
  };

  // Load available subservice links when Add Step modal opens (company only)
  useEffect(() => {
    if (!addStepModalOpen || !appointment || userData?.userType !== "company") return;

    const loadAvailableLinks = async () => {
      setAddStepLoading(true);
      try {
        const res = await servicesApi.getServiceSubServiceLinks(appointment.serviceId);
        if (!res.success || !res.data) {
          setAddStepLinks([]);
          return;
        }
        const existingIds = new Set(
          appointment.progressSteps.map((s) => s.serviceSubService.id)
        );
        const parsePrice = (val: unknown): number => {
          if (val == null) return 0;
          if (typeof val === "number" && !Number.isNaN(val)) return val;
          const n = typeof val === "string" ? parseFloat(val.replace(",", ".")) : Number(val);
          return Number.isNaN(n) ? 0 : n;
        };
        const available = res.data
          .filter((link) => !existingIds.has(link.id))
          .map((link) => ({
            id: link.id,
            subServiceName: link.subService?.name ?? `Subserviço #${link.id}`,
            price: parsePrice((link as { price?: unknown }).price),
          }));
        setAddStepLinks(available);
      } catch {
        setAddStepLinks([]);
      } finally {
        setAddStepLoading(false);
      }
    };

    loadAvailableLinks();
  }, [addStepModalOpen, appointment, userData?.userType]);

  const handleAddStep = async (serviceSubServiceId: number) => {
    if (!appointment) return;
    setAddStepSaving(serviceSubServiceId);
    try {
      const res = await appointmentsApi.createAppointmentProgress(
        appointment.id,
        serviceSubServiceId
      );
      if (res.success) {
        await fetchAppointmentDetails({ preserveScroll: true });
        // Keep modal open so user can add more steps
      }
    } finally {
      setAddStepSaving(null);
    }
  };

  const handleFinishAppointment = async () => {
    if (!appointment) return;

    setFinishConfirmOpen(false);
    setFinishLoading(true);
    setError(null);
    try {
      const response = await appointmentsApi.updateAppointment(appointment.id, {
        status: "completed",
      });

      if (response.success) {
        await fetchAppointmentDetails({ preserveScroll: true });
      } else {
        setError(response.error || "Erro ao finalizar agendamento");
      }
    } catch (err) {
      setError("Erro ao finalizar agendamento");
    } finally {
      setFinishLoading(false);
    }
  };

  const handleDownloadAppointmentPdf = async (
    options: BudgetPdfPriceOptions
  ) => {
    if (!appointment) return;

    setPdfDownloadLoading(true);
    setPdfError(null);
    try {
      const companyRes = await companyApi.getCompany();
      const companyForPdf =
        companyRes.success && companyRes.data ? companyRes.data : company;
      await downloadAppointmentPdf(appointment, companyForPdf, options);
      setPdfModalOpen(false);
    } catch (err) {
      setPdfError(
        err instanceof Error ? err.message : "Erro ao gerar comprovante"
      );
    } finally {
      setPdfDownloadLoading(false);
    }
  };

  const unclosedSteps = appointment?.progressSteps?.filter((s) => !s.endTime) ?? [];

  // Group steps by serviceSubService so same subservice appears together
  const sortedProgressSteps = useMemo(() => {
    if (!appointment?.progressSteps?.length) return [];
    return [...appointment.progressSteps].sort((a, b) => {
      const idA = a.serviceSubService?.id ?? 0;
      const idB = b.serviceSubService?.id ?? 0;
      if (idA !== idB) return idA - idB;
      return a.id - b.id;
    });
  }, [appointment?.progressSteps]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            <div className="h-4 bg-gray-300 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="p-6">
        <Card className="p-6 text-center">
          <p className="text-red-600 mb-4">
            {error || "Agendamento não encontrado"}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            Voltar
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <AdminPageHeaderWithBack
        icon={<Icons.Calendar sx={{ fontSize: 28 }} />}
        title="Detalhes do Agendamento"
        subtitle={isProfessionalCarView ? `#${appointment.id}` : `#${appointment.id} - ${appointment.client?.name || "Cliente"}`}
        onBackClick={() => navigate(-1)}
      />

      {/* Main Content - Row Layout (mobile: Progress first; xl: Info left, Progress right) */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 min-h-0">
        {/* Left Side - Appointment Info (order-2 on mobile so it appears after Progress) */}
        <div className="order-2 xl:order-1 xl:col-span-1">
          <Card className="p-6 h-fit">
            <h3 className="font-semibold text-gray-900 mb-4">
              Informações do Agendamento
            </h3>
            <div className="space-y-6">
              {/* Client Info - hide name/phone/WhatsApp for professional (car) */}
              {!isProfessionalCarView && (
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Cliente</h4>
                <div className="space-y-1">
                  
                    <>
                      <p className="text-sm flex items-center gap-1.5">
                        <span className="font-medium">Nome:</span>{" "}
                        {appointment.client?.name || "Cliente"}
                        {appointment.client && isBirthdayToday((appointment.client as any).birthday) && (
                          <Icons.Cake sx={{ fontSize: 18, color: "#f59e0b" }} titleAccess="Aniversário hoje!" />
                        )}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm">
                          <span className="font-medium">Telefone:</span>{" "}
                          {formatPhoneNumber(appointment.client?.phoneNumber || "")}
                        </p>
                        {appointment.status === "completed" && appointment.client?.phoneNumber && (
                          <button
                            onClick={() =>
                              sendWhatsAppMessage({
                                phoneNumber: appointment.client.phoneNumber!,
                                message: getCompletionMessage(appointment.client.name),
                              })
                            }
                            className={getWhatsAppButtonStyles("sm")}
                          >
                            <svg
                              className={getWhatsAppIconStyles("sm")}
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d={WHATSAPP_ICON_PATH} />
                            </svg>
                          </button>
                        )}
                      </div>
                    </>
                  
                </div>
              </div>
            )}
              {/* Car Info - Only show for car companies; color by effort */}
              {company?.category === "car" && appointment.clientCar && (
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Veículo</h4>
                  <div
                    className={`rounded-lg border px-3 py-2 text-sm ${getCarEffortColor(
                      appointment.clientCar.effort
                    )}`}
                    title={appointment.clientCar.effort ? `Esforço: ${appointment.clientCar.effort}` : undefined}
                  >
                    <p className="font-medium flex items-center gap-2">
                      <CarBrandLogo brand={appointment.clientCar.brand} />
                      <span>
                        {appointment.clientCar.model} — {appointment.clientCar.plate}
                      </span>
                    </p>
                    {appointment.clientCar.effort && (
                      <p className="text-xs font-semibold mt-1 opacity-90">
                        {appointment.clientCar.effort}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Service Info - hide price/payment for professional (car) */}
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Serviço</h4>
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Nome:</span>{" "}
                    {appointment.service?.name || "Serviço"}
                  </p>
                  {!isProfessionalCarView && (
                    <p className="text-sm">
                      <span className="font-medium">Preço:</span> R${" "}
                      {appointment.price || "0.00"}
                    </p>
                  )}
                </div>
                {!isProfessionalCarView && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="font-medium">Pagamento:</span>
                    {appointment.payment ? (
                      <button
                        onClick={() => setPaymentModalOpen(true)}
                        className={`px-2 py-1 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity ${getPaymentMethodColor(
                          appointment.payment
                        )}`}
                        title="Clique para atualizar pagamento"
                      >
                        {appointment.payment}
                      </button>
                    ) : (
                      <ActionButton
                        variant="outline"
                        size="sm"
                        icon={<Icons.Payment sx={{ fontSize: 16 }} />}
                        onClick={() => setPaymentModalOpen(true)}
                        className="text-xs px-2 py-1"
                      >
                        Pagar
                      </ActionButton>
                    )}
                  </div>
                )}
              </div>

              {/* Schedule Info */}
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Agendamento</h4>
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Data:</span>{" "}
                    {createDateInBrazil(
                      appointment.appointmentDate.includes("T")
                        ? appointment.appointmentDate.split("T")[0]
                        : appointment.appointmentDate
                    ).toLocaleDateString("pt-BR", {
                      timeZone: TIMEZONE,
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                    {appointmentSpansMultipleDays(appointment) && (
                      <>
                        {" "}
                        até{" "}
                        {formatDateShort(
                          toAppointmentDateOnly(appointment.appointmentEnd)
                        )}
                      </>
                    )}
                  </p>
                </div>
                {company?.category === "car" && !isProfessionalCarView && (
                  <ActionButton
                    variant="outline"
                    size="sm"
                    icon={<Icons.CalendarMonth sx={{ fontSize: 16 }} />}
                    onClick={() => setEndDateModalOpen(true)}
                    className="mt-2 text-xs"
                  >
                    {appointmentSpansMultipleDays(appointment)
                      ? "Alterar data de término"
                      : "Estender agendamento"}
                  </ActionButton>
                )}
              </div>

              {(appointment.notes ||
                (company?.category === "car" &&
                  (appointment.deliveryStartTime ||
                    appointment.deliveryEndTime)) ||
                !isProfessionalCarView) && (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h4 className="font-medium text-gray-800">
                      Observações e entrega
                    </h4>
                    {!isProfessionalCarView && (
                      <button
                        type="button"
                        onClick={() => setNotesModalOpen(true)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar observações e entrega"
                      >
                        <Icons.Edit sx={{ fontSize: 18 }} />
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {appointment.notes ? (
                      <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-800 px-3 py-2">
                        <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">
                          Observações
                        </p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {appointment.notes}
                        </p>
                      </div>
                    ) : (
                      !isProfessionalCarView && (
                        <p className="text-sm text-gray-500">
                          Nenhuma observação registrada.
                        </p>
                      )
                    )}
                    {company?.category === "car" &&
                      (appointment.deliveryStartTime ||
                        appointment.deliveryEndTime) && (
                        <div className="rounded-lg bg-sky-50 dark:bg-sky-950/40 border border-sky-100 dark:border-sky-800 px-3 py-2">
                          <p className="text-xs font-medium text-sky-800 mb-1">
                            Horário de entrega
                          </p>
                          <p className="text-sm text-gray-700 flex items-center gap-1.5">
                            <Icons.Schedule
                              sx={{ fontSize: 16 }}
                              className="text-sky-600"
                            />
                            {appointment.deliveryStartTime
                              ? formatTime(appointment.deliveryStartTime)
                              : "—"}
                            {appointment.deliveryStartTime &&
                            appointment.deliveryEndTime
                              ? " – "
                              : ""}
                            {appointment.deliveryEndTime
                              ? formatTime(appointment.deliveryEndTime)
                              : appointment.deliveryStartTime
                              ? ""
                              : "—"}
                          </p>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Status */}
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Status</h4>
                <div className="space-y-2">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadgeColor(
                      appointment.status
                    )}`}
                  >
                    {translateStatus(appointment.status)}
                  </span>
                  {isCompanyAdmin && appointment.status === "completed" && (
                    <div className="pt-1">
                      <ActionButton
                        variant="outline"
                        size="sm"
                        icon={<Icons.Receipt sx={{ fontSize: 16 }} />}
                        onClick={() => {
                          setPdfError(null);
                          setPdfModalOpen(true);
                        }}
                        className="text-xs"
                      >
                        Gerar comprovante
                      </ActionButton>
                      {pdfError && (
                        <p className="text-xs text-red-600 mt-2">{pdfError}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Total Duration */}
              <div>
                <h4 className="font-medium text-gray-800 mb-2">
                  Duração Total
                </h4>
                <div className="space-y-2">
                  {renderAppointmentTimer()}
                  {getSumOfStepDurations() > 0 && (
                    <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border bg-amber-100 text-amber-800 border-amber-300 ml-2">
                      <span>⏱️</span>
                      <span>{formatDuration(getSumOfStepDurations())}</span>
                      <span className="ml-1 text-xs">soma das etapas</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Side - Progress Steps (order-1 on mobile so it appears first) */}
        <div className="order-1 xl:order-2 xl:col-span-2">
          <Card className="p-6 h-full">
            <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
              {isCompanyAdmin ? (
                <div className="flex bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setDetailsTab("progress")}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                      detailsTab === "progress"
                        ? "bg-white dark:bg-slate-800 text-gray-900 dark:text-white shadow-sm"
                        : "text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    Progresso
                  </button>
                  <button
                    type="button"
                    onClick={() => setDetailsTab("followUp")}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 inline-flex items-center gap-1.5 ${
                      detailsTab === "followUp"
                        ? "bg-white dark:bg-slate-800 text-gray-900 dark:text-white shadow-sm"
                        : "text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    Retorno
                    {expiredFollowUpCount > 0 && (
                      <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-semibold">
                        {expiredFollowUpCount}
                      </span>
                    )}
                  </button>
                </div>
              ) : (
                <h3 className="font-semibold text-gray-900">
                  Progresso do Atendimento
                </h3>
              )}
              {isCompanyAdmin &&
                detailsTab === "progress" &&
                company?.category === "car" && (
                <ActionButton
                  variant="success"
                  size="sm"
                  icon={<Icons.Add sx={{ fontSize: 18 }} />}
                  onClick={() => setAddStepModalOpen(true)}
                  className="shrink-0"
                >
                  Adicionar etapa
                </ActionButton>
              )}
            </div>
            {isCompanyAdmin && (
              <div className={detailsTab === "followUp" ? "block" : "hidden"}>
                <AppointmentFollowUpPanel
                  key={followUpRefreshKey}
                  clientId={appointment.clientId}
                  clientCarId={appointment.clientCar?.id ?? appointment.clientCarId}
                  clientName={appointment.client?.name || "Cliente"}
                  clientPhone={appointment.client?.phoneNumber}
                  isCarCompany={company?.category === "car"}
                  onExpiredCountChange={setExpiredFollowUpCount}
                  onScheduleAppointment={(params) => {
                    setPreFilledAppointmentData(params);
                    setCreateModalOpen(true);
                  }}
                />
              </div>
            )}
            {detailsTab === "progress" && (
            <>
            <div className="space-y-4">
              {sortedProgressSteps.map((step, index) => {
                const status = getStepStatus(step);
                const isLast = index === sortedProgressSteps.length - 1;

                return (
                  <div key={step.id} className="flex items-start space-x-4">
                    {/* Step Indicator */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                          status === "completed"
                            ? "bg-green-100 border-green-500 text-green-600"
                            : status === "in_progress"
                            ? "bg-blue-100 border-blue-500 text-blue-600"
                            : "bg-gray-100 border-gray-300 text-gray-400"
                        }`}
                      >
                        {getStepStatusIcon(status)}
                      </div>
                      {!isLast && (
                        <div
                          className={`w-0.5 h-12 mt-2 ${
                            status === "completed"
                              ? "bg-green-300"
                              : "bg-gray-300"
                          }`}
                        ></div>
                      )}
                    </div>

                    {/* Step Content */}
                    <div className="flex-1 min-w-0 pb-8">
                      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() =>
                            handleManageProfessionals(
                              step.id.toString(),
                              step.serviceSubService?.subService?.name || "Subserviço"
                            )
                          }
                          className="flex items-center gap-2 min-w-0 flex-1 text-left rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors p-1 -m-1"
                        >
                          {step.professionals && step.professionals.length > 0 && (
                            <div className="flex items-center -space-x-2 shrink-0" title={step.professionals.map((p) => p.name).join(", ")}>
                              {step.professionals.map((prof) => (
                                <Avatar
                                  key={prof.id}
                                  text={prof.name}
                                  src={prof.picture}
                                  size="xs"
                                  variant="blue"
                                  className="ring-2 ring-white"
                                />
                              ))}
                            </div>
                          )}
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {step.serviceSubService?.subService?.name || "Subserviço"}
                          </h4>
                        </button>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium border shrink-0 ${getStepStatusColor(
                            status
                          )}`}
                        >
                          {status === "completed"
                            ? "Concluído"
                            : status === "in_progress"
                            ? "Em Andamento"
                            : "Pendente"}
                        </span>
                      </div>

                      {step.notes && (
                        <p className="text-sm text-gray-600 mb-2">
                          {step.notes}
                        </p>
                      )}

                      {/* Timer, Time Info, and Buttons Row */}
                      <div className="flex flex-wrap items-center gap-3 justify-between">
                        <div className="flex items-center gap-3 flex-wrap">
                          {/* Time Information */}
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <span className="font-medium">Início:</span>
                              <span>{formatDateTime(step.startTime)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-medium">Fim:</span>
                              <span>{formatDateTime(step.endTime)}</span>
                            </div>
                          </div>

                          {/* Step Timer */}
                          {renderStepTimer(step, status)}

                          {/* Action Buttons (step starts when a professional is assigned via Profissionais button) */}
                          <div className="flex flex-wrap gap-2">
                            {step.startTime && !step.endTime && (
                              <>
                                <ActionButton
                                  variant="secondary"
                                  size="sm"
                                  icon={<Icons.Schedule sx={{ fontSize: 18 }} />}
                                  onClick={() => {
                                    setPauseModalStep(step);
                                    setSelectedPauseReason(null);
                                  }}
                                >
                                  Pausar
                                </ActionButton>
                                <button
                                  onClick={() => handleEndStep(step.id)}
                                disabled={stepLoading[step.id]}
                                className="group relative px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 
                                   text-white text-sm font-medium rounded-lg shadow-lg hover:shadow-xl 
                                   disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg
                                   transform transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]
                                   flex items-center gap-2 overflow-hidden"
                              >
                                {/* Background Animation */}
                                <div
                                  className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-500 
                                        opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                ></div>

                                {/* Button Content */}
                                <div className="relative flex items-center gap-2">
                                  {stepLoading[step.id] ? (
                                    <>
                                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      <span className="tracking-wide">
                                        Finalizando...
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <div
                                        className="w-4 h-4 bg-white rounded-full flex items-center justify-center
                                              group-hover:scale-110 transition-transform duration-300"
                                      >
                                        <svg
                                          className="w-2.5 h-2.5 text-green-600"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      </div>
                                      <span className="tracking-wide font-semibold">
                                        Finalizar
                                      </span>
                                    </>
                                  )}
                                </div>

                                {/* Shine Effect */}
                                <div
                                  className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white to-transparent 
                                        opacity-0 group-hover:opacity-20 group-hover:animate-[shine_0.6s_ease-out]"
                                ></div>
                              </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Finish Appointment Button - at end of steps card */}
            {appointment.status.toLowerCase() !== "completed" &&
              appointment.status.toLowerCase() !== "cancelled" && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <ActionButton
                    onClick={() => setFinishConfirmOpen(true)}
                    variant="success"
                    size="sm"
                    icon={<Icons.Check sx={{ fontSize: 18 }} />}
                    disabled={finishLoading}
                    className="w-full justify-center"
                  >
                    {finishLoading ? "Finalizando..." : "Finalizar"}
                  </ActionButton>
                </div>
              )}
            </>
            )}
          </Card>
        </div>
      </div>

      {/* Payment Modal - not shown for professional (car) */}
      {appointment && !isProfessionalCarView && (
        <UpdatePaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          onSuccess={(updatedAppointment) => {
            if (updatedAppointment) {
              // Update the appointment directly in state
              setAppointment(updatedAppointment);
            }
            setPaymentModalOpen(false);
          }}
          appointment={appointment}
        />
      )}

      {appointment && !isProfessionalCarView && (
        <AppointmentEndModal
          isOpen={endDateModalOpen}
          onClose={() => setEndDateModalOpen(false)}
          appointmentId={appointment.id}
          appointmentDate={appointment.appointmentDate}
          initialAppointmentEnd={appointment.appointmentEnd}
          onSuccess={() => {
            setEndDateModalOpen(false);
            fetchAppointmentDetails({ preserveScroll: true });
          }}
        />
      )}

      {appointment && !isProfessionalCarView && (
        <AppointmentNotesModal
          isOpen={notesModalOpen}
          onClose={() => setNotesModalOpen(false)}
          appointmentId={appointment.id}
          initialNotes={appointment.notes}
          initialDeliveryStartTime={appointment.deliveryStartTime}
          initialDeliveryEndTime={appointment.deliveryEndTime}
          showDeliveryFields={company?.category === "car"}
          onSuccess={() => {
            setNotesModalOpen(false);
            fetchAppointmentDetails({ preserveScroll: true });
          }}
        />
      )}

      {createModalOpen && (
        <CreateAppointmentModal
          isOpen={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false);
            setPreFilledAppointmentData(undefined);
          }}
          onSuccess={() => {
            setCreateModalOpen(false);
            setPreFilledAppointmentData(undefined);
            setFollowUpRefreshKey((key) => key + 1);
          }}
          selectedDate={getTodayInBrazil()}
          preFilledData={preFilledAppointmentData}
        />
      )}

      {/* Finish appointment confirmation modal */}
      <Modal
        isOpen={finishConfirmOpen}
        onClose={() => setFinishConfirmOpen(false)}
        title="Confirmar finalização"
        icon={<Icons.Check sx={{ fontSize: 24 }} />}
        size="sm"
      >
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            Tem certeza que deseja finalizar este agendamento?
          </p>
          {unclosedSteps.length > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 mb-4">
              <p className="text-amber-800 font-medium text-sm mb-2">
                As seguintes etapas não foram encerradas:
              </p>
              <ul className="list-disc list-inside text-amber-700 text-sm space-y-1">
                {unclosedSteps.map((step) => (
                  <li key={step.id}>
                    {step.serviceSubService?.subService?.name || "Subserviço"}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex gap-3 justify-end">
            <ActionButton
              variant="outline"
              size="sm"
              onClick={() => setFinishConfirmOpen(false)}
            >
              Cancelar
            </ActionButton>
            <ActionButton
              variant="success"
              size="sm"
              icon={<Icons.Check sx={{ fontSize: 18 }} />}
              disabled={finishLoading}
              onClick={handleFinishAppointment}
            >
              {finishLoading ? "Finalizando..." : "Finalizar"}
            </ActionButton>
          </div>
        </div>
      </Modal>

      {/* Pause step reason modal */}
      <Modal
        isOpen={!!pauseModalStep}
        onClose={() => !pauseLoading && setPauseModalStep(null)}
        title="Pausar etapa"
        icon={<Icons.Schedule sx={{ fontSize: 24 }} />}
        headerGradient="gray"
        size="sm"
      >
        <div className="p-6">
          <p className="text-gray-700 mb-4">Selecione o motivo da pausa:</p>
          <div className="space-y-2 mb-6">
            {PAUSE_REASONS.map((reason) => (
              <button
                key={reason}
                type="button"
                onClick={() => setSelectedPauseReason(reason)}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                  selectedPauseReason === reason
                    ? "border-blue-500 bg-blue-50 text-blue-900"
                    : "border-gray-200 hover:border-gray-300 text-gray-700"
                }`}
              >
                {reason}
              </button>
            ))}
          </div>
          <div className="flex gap-3 justify-end">
            <ActionButton
              variant="outline"
              size="sm"
              onClick={() => setPauseModalStep(null)}
              disabled={pauseLoading}
            >
              Cancelar
            </ActionButton>
            <ActionButton
              variant="primary"
              size="sm"
              icon={<Icons.Schedule sx={{ fontSize: 18 }} />}
              disabled={!selectedPauseReason || pauseLoading}
              onClick={() =>
                pauseModalStep &&
                selectedPauseReason &&
                handlePauseStep(pauseModalStep.id, selectedPauseReason)
              }
            >
              {pauseLoading ? "Pausando..." : "Pausar"}
            </ActionButton>
          </div>
        </div>
      </Modal>

      {/* Step Professionals Modal */}
      {selectedStep && appointment && company && (
        <StepProfessionalsModal
          isOpen={professionalModalOpen}
          onClose={handleCloseProfessionalModal}
          stepId={selectedStep.id}
          stepName={selectedStep.name}
          appointmentId={appointment.id.toString()}
          companyId={company.id}
          onProfessionalsUpdated={() => fetchAppointmentDetails({ preserveScroll: true })}
          isCompanyUser={userData?.userType === "company"}
          onStepDeleted={() => fetchAppointmentDetails({ preserveScroll: true })}
        />
      )}

      {/* Add Step Modal - company only */}
      <Modal
        isOpen={addStepModalOpen}
        onClose={() => setAddStepModalOpen(false)}
        title="Adicionar etapa"
        icon={<Icons.Add sx={{ fontSize: 24 }} />}
      >
        <div className="p-6">
          {addStepLoading ? (
            <p className="text-gray-500 text-center py-6">Carregando...</p>
          ) : addStepLinks.length === 0 ? (
            <p className="text-gray-500 text-center py-6">
              Nenhum subserviço disponível para adicionar. Todos os subserviços deste serviço já estão nas etapas.
            </p>
          ) : (
            <ul className="space-y-2 max-h-80 overflow-y-auto">
              {addStepLinks.map((link) => (
                <li key={link.id}>
                  <button
                    type="button"
                    onClick={() => handleAddStep(link.id)}
                    disabled={addStepSaving !== null}
                    className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-3"
                  >
                    <span className="font-medium text-gray-900">
                      <span className="text-emerald-700 font-semibold mr-2">
                        R$ {(typeof link.price === "number" ? link.price : parseFloat(String(link.price)) || 0).toFixed(2).replace(".", ",")}
                      </span>
                      {link.subServiceName}
                    </span>
                    {addStepSaving === link.id ? (
                      <span className="text-sm text-gray-500">Adicionando...</span>
                    ) : (
                      <span className="text-emerald-600 text-sm font-medium">+ Adicionar</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>

      <DownloadBudgetPdfModal
        isOpen={pdfModalOpen}
        budgetLabel="serviços realizados"
        loading={pdfDownloadLoading}
        onClose={() => {
          if (!pdfDownloadLoading) setPdfModalOpen(false);
        }}
        onConfirm={handleDownloadAppointmentPdf}
      />
    </div>
  );
};
